'use es6';

import { createSelector } from 'reselect';
import Categories from 'ContentUtils/constants/Categories';
import { ContentTypes, GATES, MultiLanguageContentTypeScopes, LANDING_PAGES_TRUSTED_SCOPE } from '../constants/base';
import { getMatchContentSlugType } from './matchSelectors';
import { PATAGONIA_EDITOR_REDESIGN_OPTIN_NAME } from '../constants/optins';
function getScopePrefix(contentType) {
  let prefix;
  switch (contentType) {
    case ContentTypes.SITE_PAGE:
      prefix = 'pages';
      break;
    case ContentTypes.LANDING_PAGE:
      prefix = 'landingpages';
      break;
    case ContentTypes.BLOG_POST:
    case ContentTypes.BLOG_LISTING_PAGE:
      prefix = 'blog';
      break;
    default:
      prefix = 'pages';
  }
  return prefix;
}
export const getAuth = state => state.auth;
const getAuthByName = setting => createSelector([getAuth], auth => auth.get(setting));
export const getAuthGates = getAuthByName('gates');
export const getAuthUser = getAuthByName('user');
export const getAuthPortalScopes = getAuthByName('portalScopes');
export const getAuthPortal = getAuthByName('portal');
const getOptIns = getAuthByName('optIns');
export const getOptInsAsJS = createSelector([getOptIns], optIns => optIns ? optIns.toJS() : {});
export const getAuthPortalLimits = createSelector([getAuthPortal], portal => portal.get('limits'));
const getAuthUserByName = setting => createSelector([getAuthUser], user => user.get(setting));
export const getAuthUserEmail = getAuthUserByName('email');
export const getAuthUserScopes = getAuthUserByName('scopes');
export const getAuthUserUserId = getAuthUserByName('user_id');
const getHasUserGatesByName = name => createSelector([getAuthGates], gates => gates.includes(name));
export const getUserHasAccountTrust = createSelector([getAuthUserScopes], scopes => scopes.includes(LANDING_PAGES_TRUSTED_SCOPE));
export const getUserHasWriteAccess = createSelector([getAuthUserScopes, getMatchContentSlugType, getUserHasAccountTrust], (scopes, contentType) => {
  const prefix = getScopePrefix(contentType);
  if (!prefix) {
    return false;
  }
  return scopes.includes(`${prefix}-write`);
});
export const getUserHasReadAccess = createSelector([getAuthUserScopes, getMatchContentSlugType], (scopes, contentType) => {
  if (contentType === ContentTypes.EXTERNAL_PAGE) return true;
  const prefix = getScopePrefix(contentType);
  if (!prefix) {
    return false;
  }
  return scopes.includes(`${prefix}-access`);
});
const getUserScopeByName = name => createSelector([getAuthUserScopes], scopes => scopes.includes(name));
export const getUserHasPublishAccess = createSelector([getAuthUserScopes, getMatchContentSlugType, getUserHasWriteAccess, getUserHasAccountTrust], (scopes, contentType, hasWriteAccess, hasAccountTrust) => {
  const prefix = getScopePrefix(contentType);
  if (!prefix) {
    return false;
  }
  return hasWriteAccess && scopes.includes(`${prefix}-publish`) && hasAccountTrust;
});
export const getUserHasMultiLanguageAccess = createSelector([getMatchContentSlugType, getAuthUserScopes], (contentType, scopes) => scopes.includes(MultiLanguageContentTypeScopes[contentType]));
export const getUserHasPageSubcategoryMoveAccess = getUserScopeByName('page-subcategory-move');
export const getUserCanCreateAbTest = getUserScopeByName('content-ab-testing');
export const getUserHasSitePageAccess = getUserScopeByName('sitepages-access');
export const getUserHasLandingPageAccess = getUserScopeByName('landingpages-access');
export const getUserHasAdvancedLPAccess = getUserScopeByName('landingpages-advanced-write');
export const getUserHasBlogAccess = getUserScopeByName('blog-access');
export const getUserHasSocialAccess = getUserScopeByName('social-access');
export const getUserHasAdsAccess = getUserScopeByName('ads-write');
export const getUserHasMarketableContactsAccess = getUserScopeByName('marketable-contacts-access');
export const getUserHasContactsExportAccess = getUserScopeByName('crm-export');
export const getUserHasGoogleSearchConsoleAccess = getUserScopeByName('google-search-console-access');
const getUserHasContactsListAccess = getUserScopeByName('contacts-lists-access');
const getUserHasContactsListCreateSimple = getUserScopeByName('contacts-lists-create-simple');
const getUserHasContactsListCreateAdvanced = getUserScopeByName('contacts-lists-create-advanced');
export const getUserHasContactsListCreateAccess = createSelector([getUserHasContactsListAccess, getUserHasContactsListCreateSimple, getUserHasContactsListCreateAdvanced], (listAccess, simpleCreate, advancedCreate) => listAccess && (simpleCreate || advancedCreate));
export const getUserHasVideo = getUserScopeByName('marketing-video');
const getUserHasThemedContentCreatorAccess = getUserScopeByName('themed-content-creator-access');
const getUserHasMarketplaceAssetProvider = getUserScopeByName('marketplace-asset-provider');
export const getUserHasAudienceAccessScope = getUserScopeByName('marketing-content-restricted-write');
export const getUserHasAudienceAccess = createSelector([getUserHasAudienceAccessScope, getUserHasPublishAccess], (hasAudienceAccessScope, hasPublishScope) => hasAudienceAccessScope && hasPublishScope);
export const getHasMABAccess = getUserScopeByName('content-mab-testing');
export const getUserHasCampaignsAccess = getUserScopeByName('campaigns-access');
export const getUserHasTemplateAccess = getUserScopeByName('design-manager-access');
export const getUserHasRevenueAttributionAccess = getUserScopeByName('revenue-attribution-read');
export const getUserHasAnalyticsAccess = getUserScopeByName('website-analytics');
export const getUserHasAttributionLiteAccess = getUserScopeByName('revenue-attribution-lite');
const getHasPortalScope = name => createSelector([getAuthPortalScopes], portalScopes => portalScopes.includes(name));
export const getPortalHasAbTesting = getHasPortalScope('content-ab-testing');
export const getUserHasEitherTestAccess = createSelector([getUserCanCreateAbTest, getHasMABAccess], (hasAbTestAccess, hasMABTestAccess) => hasAbTestAccess || hasMABTestAccess);
export const getPortalHasAdsRoiReporting = getHasPortalScope('ads-roi-reporting');
const getPortalHasSitePagesNoBranding = getHasPortalScope('website-pages-no-branding');
const getPortalHasLandingPagesNoBranding = getHasPortalScope('landingpages-no-branding');
const getPortalHasBlogPagesNoBranding = getHasPortalScope('blog-pages-no-branding');
export const getHasFreeSitePages = createSelector([getPortalHasSitePagesNoBranding], hasSitePagesNoBranding => !hasSitePagesNoBranding);
export const getHasFreeLandingPages = createSelector([getPortalHasLandingPagesNoBranding], hasLandingPagesNoBranding => !hasLandingPagesNoBranding);
export const getHasFreeBlogContent = createSelector([getPortalHasBlogPagesNoBranding], hasBlogPagesNoBranding => !hasBlogPagesNoBranding);
const getHasCmsHub = createSelector([getUserHasSitePageAccess, getUserHasThemedContentCreatorAccess], (hasSitePageAccess, hasThemedContentCreatorAccess) => hasSitePageAccess && hasThemedContentCreatorAccess);

// Ensure portal has access to CMS Hub and is not a free/pro/enterprise portal or marketplace provider account
export const getIsCmsStarterPortal = createSelector([getHasCmsHub, getPortalHasSitePagesNoBranding, getUserHasMarketplaceAssetProvider], (hasCmsHub, hasWebsitePagesNoBranding, hasMarketplaceAssetProvider) => hasCmsHub && hasWebsitePagesNoBranding && !hasMarketplaceAssetProvider);

// Multi-language pages only count against the site pages limit if
// the portal is a CMS Free or Starter portal and has the new bumped up
// site page limit
export const getCanCreateMlangPagesIfSitePagesLimitReached = createSelector([getIsCmsStarterPortal, getHasFreeSitePages], (isCmsStarterPortal, hasFreeSitePages) => isCmsStarterPortal || hasFreeSitePages);

// Multi-language blog posts only count against the blog posts limit if
// the portal is a CMS Free or Starter portal and is ungated for
// the CMS Free beta
export const getCanCreateMlangBlogPostsIfLimitReached = createSelector([getIsCmsStarterPortal, getHasFreeBlogContent], (isCmsStarterPortal, hasFreeBlogContent) => isCmsStarterPortal || hasFreeBlogContent);
export const selectHasPerformanceTabAccess = createSelector([getMatchContentSlugType, getUserHasAnalyticsAccess], (contentType, hasAnalyticsAccess) => {
  switch (contentType) {
    case ContentTypes.BLOG_LISTING_PAGE:
      {
        return hasAnalyticsAccess;
      }
    case ContentTypes.EXTERNAL_PAGE:
      {
        return false;
      }
    default:
      {
        return true;
      }
  }
});
const getHasLandingPagesAutoTranslateAccess = getHasPortalScope('landingpages-auto-translate');
const getHasSitePagesAutoTranslateAccess = getHasPortalScope('sitepages-auto-translate');
const getHasBlogPostsAutoTranslateAccess = getHasPortalScope('blog-posts-auto-translate');
export const getHasAutoTranslateAccess = contentType => createSelector([getHasLandingPagesAutoTranslateAccess, getHasSitePagesAutoTranslateAccess, getHasBlogPostsAutoTranslateAccess], (hasLandingPagesAutoTranslateAccess, hasSitePagesAutoTranslateAccess, hasBlogPostsAutoTranslateAccess) => {
  switch (contentType) {
    case ContentTypes.LANDING_PAGE:
      return hasLandingPagesAutoTranslateAccess;
    case ContentTypes.SITE_PAGE:
      return hasSitePagesAutoTranslateAccess;
    case ContentTypes.BLOG_POST:
      return hasBlogPostsAutoTranslateAccess;
    default:
      return false;
  }
});
export const getHasAccessToCmsApprovals = contentType => createSelector([getAuthUserScopes], scopes => {
  switch (contentType) {
    case ContentTypes.LANDING_PAGE:
      return scopes.includes('landingpages-approval-read');
    case ContentTypes.SITE_PAGE:
      return scopes.includes('pages-approval-read');
    case ContentTypes.BLOG_POST:
      return scopes.includes('blog-approval-read');
    default:
      return false;
  }
});
const getHasLandingPagesPasswordProtection = getUserScopeByName('landingpages-password-protection');
const getHasSitePagesPasswordProtection = getUserScopeByName('sitepages-password-protection');

// TODO: remove once 100% cutover to memberships access control component
export const getHasPasswordProtectionAccess = contentType => createSelector([getHasLandingPagesPasswordProtection, getHasSitePagesPasswordProtection], (hasLandingPagesPasswordProtection, hasSitePagesPasswordProtection) => {
  switch (contentType) {
    case ContentTypes.LANDING_PAGE:
      return hasLandingPagesPasswordProtection;
    case ContentTypes.SITE_PAGE:
      return hasSitePagesPasswordProtection;
    default:
      return false;
  }
});
export const getHasPasswordProtectionAccessByContentCategoryId = contentCategoryId => createSelector([getHasLandingPagesPasswordProtection, getHasSitePagesPasswordProtection], (hasLandingPagesPasswordProtection, hasSitePagesPasswordProtection) => {
  switch (contentCategoryId) {
    case Categories.landing_page.id:
      return hasLandingPagesPasswordProtection;
    case Categories.site_page.id:
      return hasSitePagesPasswordProtection;
    default:
      return false;
  }
});
export const getIsUngatedForOneClickPublishFlowOnly = getHasUserGatesByName('ContentEditors:PublishImmediatelyFlow');
const getIsUngatedForPatagoniaEditor = getHasUserGatesByName(GATES.PATAGONIA_REDESIGN);
const getIsOptedIntoPatagonia = createSelector([getOptIns], optIns => optIns && optIns.get(PATAGONIA_EDITOR_REDESIGN_OPTIN_NAME));
export const getIsUsingPatagoniaEditor = createSelector([getIsUngatedForPatagoniaEditor, getIsOptedIntoPatagonia], (isUngatedForPatagonia, isOptedIntoPatagonia) => isUngatedForPatagonia && isOptedIntoPatagonia);
export const getIsUngatedForOneClickPublishFlow = createSelector([getIsUngatedForOneClickPublishFlowOnly, getIsUngatedForPatagoniaEditor], (isUngatedForOneClickPublishFlow, isUngatedForPatagoniaEditor) => isUngatedForOneClickPublishFlow && isUngatedForPatagoniaEditor);
export const getPortalIsUngatedForMembershipsBlogDataWall = getHasUserGatesByName(GATES.BLOG_DATA_WALL);
export const getPortalIsUngatedForFollowUpRegistrationEmails = getHasUserGatesByName(GATES.FOLLOW_UP_REGISTRATION_EMAILS);
const _getIsUngatedForAutotranslation = getHasUserGatesByName('CMS:Autotranslation');
export const getIsUngatedForAutotranslation = createSelector([_getIsUngatedForAutotranslation, getIsUsingPatagoniaEditor], (isUngatedForAutotranslation, isUsingPatagonia) => isUngatedForAutotranslation && isUsingPatagonia);
export const getPortalIsUngatedForPublishPrivateContentPermissions = getHasUserGatesByName(GATES.PUBLISH_PRIVATE_CONTENT);
export const getUserHasPublishPrivateContentAccess = createSelector([getAuthUserScopes, getPortalIsUngatedForPublishPrivateContentPermissions], (scopes, portalIsUngatedForPublishPrivateContentPermissions) => portalIsUngatedForPublishPrivateContentPermissions ? scopes.includes('cms-private-content-publish') : true);
export const getPortalIsUngatedForAccessControlLibrary = getHasUserGatesByName(GATES.ACCESS_CONTROL_LIBRARY);
export const getPortalIsUngatedForAccessGroups = getHasUserGatesByName(GATES.ACCESS_GROUPS);
export const getIsUngatedForFetchFromSharedPageLimitsApi = getHasUserGatesByName('CMSPublishing:FetchFromSharedPageLimitsApi');
export const getIsUngatedForSharedPageLimits = getHasUserGatesByName('CMSPublishing:SharedPageLimits');
export const getUserHasLandingPagesMultiLanguageAccess = getUserScopeByName('landingpages-multilanguage');
export const getIsUngatedForContentHubUGPs = getHasUserGatesByName('CMSPublishing:ContentHubUGPs');